import React, { useRef, useEffect, useState } from "react";
import "./RichTextEditor.css";
import { API_BASE_URL } from "../config";
import { getToken } from "../lib/tokenStorage";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  label?: string;
  className?: string;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Enter text...",
  rows = 6,
  label,
  className = "",
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [htmlContent, setHtmlContent] = useState(value);
  const isUserEditingRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (editorRef.current && !isHtmlMode && !isUserEditingRef.current) {
      editorRef.current.innerHTML = value;
    }
  }, [value, isHtmlMode]);

  const execCommand = (command: string, value?: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      // Enable CSS styling for better formatting
      try {
        document.execCommand("styleWithCSS", false, "true");
      } catch (e) {
        // styleWithCSS not supported, continue anyway
      }
      document.execCommand(command, false, value);
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      isUserEditingRef.current = true;
      onChange(editorRef.current.innerHTML);

      // Reset the flag after a short delay to allow external updates after user stops typing
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 500);
    }
  };

  const toggleHtmlMode = () => {
    if (isHtmlMode) {
      // Switch from HTML to visual mode
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlContent;
        onChange(htmlContent);
      }
    } else {
      // Switch from visual to HTML mode
      if (editorRef.current) {
        setHtmlContent(editorRef.current.innerHTML);
      }
    }
    setIsHtmlMode(!isHtmlMode);
  };

  const handleHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHtmlContent(e.target.value);
    onChange(e.target.value);
  };

  const insertLink = () => {
    const url = prompt("Enter URL:");
    if (url) {
      execCommand("createLink", url);
    }
  };

  const insertImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        uploadImage(file);
      }
    };
    input.click();
  };

  const uploadImage = async (file: File) => {
    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append("upload", file);

      const token = getToken();
      if (!token) {
        throw new Error("Not authenticated. Please log in first.");
      }

      const response = await fetch(`${API_BASE_URL}/upload.php`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Upload failed");
      }

      const data = await response.json();
      if (data.success && data.url) {
        execCommand("insertImage", data.url);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      alert(`Image upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    execCommand("insertText", text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case "b":
          e.preventDefault();
          execCommand("bold");
          break;
        case "i":
          e.preventDefault();
          execCommand("italic");
          break;
        case "u":
          e.preventDefault();
          execCommand("underline");
          break;
        default:
          break;
      }
    }
  };

  const toolbarButtons = [
    { command: "bold", icon: "fa-bold", title: "Bold" },
    { command: "italic", icon: "fa-italic", title: "Italic" },
    { command: "underline", icon: "fa-underline", title: "Underline" },
    {
      command: "strikeThrough",
      icon: "fa-strikethrough",
      title: "Strikethrough",
    },
    { command: "separator" },
    { command: "justifyLeft", icon: "fa-align-left", title: "Align Left" },
    {
      command: "justifyCenter",
      icon: "fa-align-center",
      title: "Align Center",
    },
    { command: "justifyRight", icon: "fa-align-right", title: "Align Right" },
    { command: "separator" },
    {
      command: "insertUnorderedList",
      icon: "fa-list-ul",
      title: "Bullet List",
    },
    {
      command: "insertOrderedList",
      icon: "fa-list-ol",
      title: "Numbered List",
    },
    { command: "separator" },
    { command: "indent", icon: "fa-indent", title: "Indent" },
    { command: "outdent", icon: "fa-outdent", title: "Outdent" },
    { command: "separator" },
    {
      command: "link",
      icon: "fa-link",
      title: "Insert Link",
      handler: insertLink,
    },
    {
      command: "image",
      icon: "fa-image",
      title: "Insert Image",
      handler: insertImage,
    },
    {
      command: "removeFormat",
      icon: "fa-remove-format",
      title: "Remove Formatting",
    },
  ];

  const formatOptions = [
    { value: "", label: "Format" },
    { value: "h1", label: "Heading 1" },
    { value: "h2", label: "Heading 2" },
    { value: "h3", label: "Heading 3" },
    { value: "h4", label: "Heading 4" },
    { value: "p", label: "Paragraph" },
  ];

  return (
    <div className={`rich-text-editor ${className}`}>
      {label && <label className="form-label">{label}</label>}

      <div className="border rounded">
        {/* Toolbar */}
        <div className="toolbar border-bottom p-2 bg-light d-flex align-items-center flex-wrap gap-1">
          {/* Format Dropdown */}
          <select
            className="form-select form-select-sm me-2"
            style={{ width: "auto" }}
            disabled={isPreviewMode || isHtmlMode}
            onChange={(e) => {
              if (e.target.value) {
                execCommand("formatBlock", e.target.value);
                e.target.value = "";
              }
            }}
          >
            {formatOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Toolbar Buttons */}
          {toolbarButtons.map((button, index) => {
            if (button.command === "separator") {
              return <div key={index} className="vr mx-1"></div>;
            }

            return (
              <button
                key={button.command}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={button.handler || (() => execCommand(button.command))}
                title={button.title}
                style={{ width: "32px", height: "32px" }}
                disabled={
                  isPreviewMode ||
                  isHtmlMode ||
                  (button.command === "image" && isUploading)
                }
              >
                <i className={`fa ${button.icon}`}></i>
                {button.command === "image" && isUploading && (
                  <span
                    className="spinner-border spinner-border-sm position-absolute"
                    style={{ width: "16px", height: "16px" }}
                  />
                )}
              </button>
            );
          })}

          <div className="vr mx-1"></div>

          {/* Mode Toggles */}
          <div className="ms-auto d-flex align-items-center gap-2">
            <button
              type="button"
              className={`btn btn-sm ${
                isPreviewMode ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              title="Toggle preview mode"
            >
              <i className="fa fa-eye"></i>
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                isHtmlMode ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={toggleHtmlMode}
              title="Toggle HTML view"
              disabled={isPreviewMode}
            >
              <i className="fa fa-code"></i>
            </button>
          </div>
        </div>

        {/* Editor Content */}
        <div className="editor-content">
          {isPreviewMode ? (
            <div
              className="form-control border-0"
              style={{
                minHeight: `${rows * 1.5}rem`,
                backgroundColor: "#f8f9fa",
              }}
              dangerouslySetInnerHTML={{ __html: value }}
            />
          ) : isHtmlMode ? (
            <textarea
              className="form-control border-0"
              value={htmlContent}
              onChange={handleHtmlChange}
              rows={rows}
              placeholder={placeholder}
              style={{ fontFamily: "monospace", fontSize: "14px" }}
            />
          ) : (
            <div
              ref={editorRef}
              contentEditable
              className="form-control border-0"
              onInput={handleInput}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              style={{ minHeight: `${rows * 1.5}rem`, outline: "none" }}
              data-placeholder={placeholder}
              suppressContentEditableWarning={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}
